<!-- Grouped End button with drop-up for end-in-past / discard. Mirrors TimerControlDropdown split-button pattern. -->
<template>
  <div class="field has-addons context-end-group">
    <div class="control">
      <button
        class="button is-small context-active__stop"
        :class="{ 'is-loading': store.contextLoading }"
        @click="endNow"
        title="End Context"
      >
        End
      </button>
    </div>
    <div class="control">
      <div class="dropdown is-up is-right" :class="{ 'is-active': isOpen }" ref="wrapper">
        <div class="dropdown-trigger">
          <button
            class="button is-small context-active__stop context-end-group__caret"
            @click.stop="isOpen = !isOpen"
            aria-label="End context at past time"
          >
            <span class="icon is-small">
              <FontAwesomeIcon :icon="faEllipsisVertical" />
            </span>
          </button>
        </div>
        <div class="dropdown-menu" role="menu">
          <div class="dropdown-content">
            <a class="dropdown-item" @click="endMinutesAgo(5)">5 min ago</a>
            <a class="dropdown-item" @click="endMinutesAgo(10)">10 min ago</a>
            <a class="dropdown-item" @click="endMinutesAgo(20)">20 min ago</a>
            <hr class="dropdown-divider">
            <a class="dropdown-item" @click="openCustomPicker">Custom time...</a>
            <hr class="dropdown-divider">
            <a class="dropdown-item has-text-danger" @click="handleDiscard">
              <span class="icon"><FontAwesomeIcon :icon="faTrash" /></span>
              <span>Discard</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="showCustomPicker" class="modal sp-modal-card is-active">
      <div class="modal-background" @click="cancelCustomPicker"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">End Context At</p>
          <button class="delete" @click="cancelCustomPicker"></button>
        </header>
        <section class="modal-card-body">
          <TimePicker
            v-model="customTime"
            label="End time:"
            :max-date="new Date()"
            :min-date="contextStartDate"
            :error="customTimeError"
          />
          <p class="help">
            Started {{ formattedMinEndTime }} · Duration {{ customDuration || '—' }}
          </p>
        </section>
        <footer class="modal-card-foot">
          <div class="buttons">
            <button class="button is-primary" @click="handleCustomTime" :disabled="!customTime">
              End
            </button>
            <button class="button" @click="cancelCustomPicker">Cancel</button>
          </div>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faEllipsisVertical, faTrash } from '@fortawesome/free-solid-svg-icons'
import TimePicker from './TimePicker.vue'
import { formatTime, formatDuration } from '../utils/time'

const store = useBlocksStore()
const wrapper = ref(null)
const isOpen = ref(false)
const showCustomPicker = ref(false)
const customTime = ref('')
const customTimeError = ref('')

const contextStartDate = computed(() => {
  return store.activeContext?.started_at ? new Date(store.activeContext.started_at) : new Date()
})

const formattedMinEndTime = computed(() => {
  const minDate = contextStartDate.value
  const now = new Date()

  if (minDate.toDateString() === now.toDateString()) {
    return formatTime(minDate)
  }

  return minDate.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
})

const customDuration = computed(() => {
  if (!customTime.value) return ''
  const end = new Date(customTime.value)
  const start = contextStartDate.value
  if (end <= start) return ''
  return formatDuration(Math.floor((end - start) / 1000))
})

function roundToMinute(date) {
  const rounded = new Date(date)
  rounded.setSeconds(0, 0)
  return rounded
}

function buildDefaultCustomEndTime() {
  const startDate = roundToMinute(contextStartDate.value)
  const oneHourLater = new Date(startDate.getTime() + 60 * 60 * 1000)
  const nextMidnight = new Date(startDate)
  nextMidnight.setHours(24, 0, 0, 0)
  const now = roundToMinute(new Date())

  const cappedTime = new Date(Math.min(oneHourLater.getTime(), nextMidnight.getTime(), now.getTime()))

  return cappedTime < startDate ? startDate : cappedTime
}

function endNow() {
  isOpen.value = false
  store.stopContext()
}

function endMinutesAgo(minutes) {
  isOpen.value = false
  const t = new Date()
  t.setMinutes(t.getMinutes() - minutes)
  store.stopContext({ stoppedAt: t.toISOString() })
}

function openCustomPicker() {
  isOpen.value = false
  customTime.value = buildDefaultCustomEndTime().toISOString()
  customTimeError.value = ''
  showCustomPicker.value = true
}

function handleCustomTime() {
  if (!customTime.value) return
  customTimeError.value = ''

  const target = new Date(customTime.value)
  if (target > new Date()) {
    customTimeError.value = 'Time cannot be in the future'
    return
  }
  if (target < contextStartDate.value) {
    customTimeError.value = 'Time is before context started'
    return
  }

  showCustomPicker.value = false
  customTime.value = ''
  store.stopContext({ stoppedAt: target.toISOString() })
}

function cancelCustomPicker() {
  showCustomPicker.value = false
  customTime.value = ''
  customTimeError.value = ''
}

function handleDiscard() {
  isOpen.value = false
  if (!confirm('Discard this context? It will be deleted without recording.')) return
  store.deleteContext(store.activeContext.id)
}

function onClickOutside(e) {
  if (wrapper.value && !wrapper.value.contains(e.target)) {
    isOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', onClickOutside, true))
onUnmounted(() => document.removeEventListener('click', onClickOutside, true))
</script>

<style scoped>
.context-end-group {
  flex-shrink: 0;
  margin-bottom: 0 !important;
  margin-left: 0.25rem !important;
}

.context-end-group .context-active__stop {
  background: rgba(213, 99, 45, 0.12);
  border-color: rgba(213, 99, 45, 0.16);
  color: var(--timist-context-orange, #d5632d);
}

.context-end-group .context-active__stop:hover {
  background: rgba(213, 99, 45, 0.18);
}

.context-end-group__caret {
  min-width: 2.15rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.dropdown-menu {
  min-width: 180px;
}
</style>
